<template>
  <div>
    <!-- Encabezado -->
    <div class="flex items-center justify-between mb-6">
      <button 
        class="mr-2 p-2 text-white bg-teal-800 hover:bg-teal-900 rounded transition-colors" 
        @click="back()"
        title="Volver atrás"
      >
        <i class="fa fa-arrow-left"></i>
      </button>
      <h1 class="text-3xl font-bold">Gestión de Inventario</h1>
    </div>

    <Head title="Gestión de Inventario" />

    <!-- Buscador de productos -->
    <div class="bg-white rounded-lg shadow-md overflow-hidden mb-6">
      <div class="p-6 border-b">
        <h3 class="text-xl font-semibold">Búsqueda de Productos</h3>
        
        <div class="mt-4">
          <div class="w-full lg:w-1/2">
            <label class="block text-gray-700 font-semibold mb-2 text-sm">Buscador avanzado (nombre, código, marca, categoría, depósito, etc.)</label>
            <search-filter
              v-model="form.search.terms"
              class="w-full"
              @reset="form.search.terms = ''"
            />
          </div>
        </div>
      </div>

      <!-- Tabla de resultados de búsqueda -->
      <div class="overflow-x-auto">
        <table class="w-full whitespace-nowrap">
          <thead>
            <tr class="text-left bg-gray-50">
              <th class="py-3 px-6 font-semibold">Código</th>
              <th class="py-3 px-6 font-semibold">Producto</th>
              <th class="py-3 px-6 font-semibold">Disponibles</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="product in products.data" 
              :key="product.id"
              class="hover:bg-gray-50 cursor-pointer transition-colors"
              @click="addProduct(product)"
            >
              <td class="py-3 px-6">{{ product?.code }}</td>
              <td class="py-3 px-6">{{ product?.name }}</td>
              <td class="py-3 px-6">{{ product?.quantity }}</td>
            </tr>
            <tr v-if="!products?.data?.length">
              <td colspan="3" class="py-4 px-6 text-center text-gray-500">
                No se encontraron productos
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Lista de productos seleccionados -->
    <div class="bg-white rounded-lg shadow-md overflow-hidden">
      <div class="p-6 border-b">
        <h3 class="text-xl font-semibold">Productos Seleccionados</h3>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full whitespace-nowrap">
          <thead>
            <tr class="text-left bg-gray-50">
              <th class="py-3 px-6 font-semibold">Cantidad</th>
              <th class="py-3 px-6 font-semibold">Depósito</th>
              <th class="py-3 px-6 font-semibold">Código</th>
              <th class="py-3 px-6 font-semibold">Producto</th>
              <th class="py-3 px-6 font-semibold">Stock Actual</th>
              <th class="py-3 px-6"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="product in items" :key="product.id" class="hover:bg-gray-50">
              <td class="py-3 px-6">
                <text-input
                  v-model="quantities.qtys[product.id]"
                  type="number"
                  min="1"
                  placeholder="Cantidad"
                  class="w-24"
                />
              </td>
              <td class="py-3 px-6">
                <select-input 
                  v-model="quantities.deposits[product.id]"
                  class="w-48"
                >
                  <option value="">Seleccionar depósito</option>
                  <option 
                    v-for="deposit in deposits" 
                    :key="deposit.id"
                    :value="deposit.name"
                  >
                    {{ deposit.name }}
                  </option>
                </select-input>
              </td>
              <td class="py-3 px-6">{{ product.code }}</td>
              <td class="py-3 px-6">{{ product.name }}</td>
              <td class="py-3 px-6">{{ product.quantity }}</td>
              <td class="py-3 px-6">
                <button
                  v-if="product.has_imei"
                  @click.prevent="openImeiModal(product)"
                  class="p-2 mr-2 text-white bg-indigo-500 hover:bg-indigo-600 rounded transition-colors relative"
                  title="Ingresar IMEIs"
                >
                  <i class="fa fa-barcode"></i>
                  <span v-if="quantities.imeis[product.id] && quantities.imeis[product.id].length > 0" class="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                    {{ quantities.imeis[product.id].length }}
                  </span>
                </button>
                <button 
                  @click="removeProduct(product)"
                  class="p-2 text-red-600 hover:text-red-800 transition-colors"
                  title="Eliminar producto"
                >
                  <i class="fa fa-trash"></i>
                </button>
              </td>
            </tr>
            <tr v-if="!items.length">
              <td colspan="6" class="py-4 px-6 text-center text-gray-500">
                No hay productos agregados
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="p-6 bg-gray-50">
        <loading-button 
          class="btn-blue"
          :loading="quantities.processing"
          @click="generate"
        >
          <i class="fa fa-save mr-2"></i>
          Guardar Entrada de Productos
        </loading-button>
      </div>
    </div>

    <!-- Modal IMEI -->
    <div v-if="imeiModal.show" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div 
        class="bg-white rounded-lg shadow-xl w-full max-w-md flex flex-col overflow-hidden my-auto"
        style="max-height: 85vh;"
      >
        <div class="px-6 py-4 border-b flex justify-between items-center shrink-0">
          <h3 class="text-lg font-bold flex items-center gap-2">
            <i class="fa fa-barcode text-indigo-600"></i>
            <span>Ingresar IMEIs</span>
          </h3>
          <button @click="closeImeiModal" class="text-gray-400 hover:text-gray-600">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <div 
          class="p-6 flex-1 overflow-y-auto"
          style="max-height: calc(85vh - 130px);"
        >
          <p class="mb-4 text-sm text-gray-600">
            Producto: <strong>{{ imeiModal.product?.name }}</strong><br>
            Cantidad a ingresar: <strong>{{ quantities.qtys[imeiModal.product?.id] || 0 }}</strong>
          </p>
          <div v-if="!(quantities.qtys[imeiModal.product?.id] > 0)" class="text-red-500 text-sm mb-4">
            Debe ingresar una cantidad mayor a 0 primero.
          </div>
          <div v-else>
            <div class="mb-4 p-3 bg-blue-50 text-blue-800 rounded-lg text-xs leading-relaxed">
              <p>Cantidad requerida: <strong>{{ quantities.qtys[imeiModal.product?.id] || 0 }}</strong> IMEIs (exactamente 15 dígitos numéricos por IMEI).</p>
            </div>
            <div v-for="i in Number(quantities.qtys[imeiModal.product?.id])" :key="i" class="mb-3">
              <div class="flex justify-between items-center mb-1">
                <label :for="`inv-imei-${i}`" class="text-xs font-semibold text-gray-700">
                  IMEI {{ i }} *
                </label>
                <span v-if="imeiModal.imeis[i-1] && !/^\d{15}$/.test(imeiModal.imeis[i-1].trim())" class="text-xs text-red-500 font-medium">
                  {{ imeiModal.imeis[i-1].trim().length }} / 15 dígitos
                </span>
                <span v-else-if="imeiModal.imeis[i-1] && /^\d{15}$/.test(imeiModal.imeis[i-1].trim())" class="text-xs text-green-600 font-semibold flex items-center gap-1">
                  <i class="fa fa-check"></i> Válido (15 dígitos)
                </span>
              </div>
              <input
                :id="`inv-imei-${i}`"
                v-model="imeiModal.imeis[i-1]"
                type="text"
                maxlength="15"
                placeholder="Escanee o escriba el IMEI de 15 dígitos"
                class="w-full border rounded-lg p-2 text-sm font-mono focus:ring-2 focus:ring-indigo-500 transition-colors"
                :class="{
                  'border-red-400 bg-red-50 text-red-900': imeiModal.imeis[i-1] && !/^\d{15}$/.test(imeiModal.imeis[i-1].trim()),
                  'border-green-500 bg-green-50 text-green-900': imeiModal.imeis[i-1] && /^\d{15}$/.test(imeiModal.imeis[i-1].trim()),
                  'border-gray-300': !imeiModal.imeis[i-1]
                }"
                @keydown.enter.prevent="handleImeiEnter(i)"
              />
            </div>
          </div>
        </div>
        <div class="px-6 py-4 bg-gray-50 border-t flex justify-end gap-2 shrink-0">
          <button @click="closeImeiModal" class="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">
            Cancelar
          </button>
          <button 
            @click="saveImeis" 
            class="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg"
            :disabled="!(quantities.qtys[imeiModal.product?.id] > 0)"
          >
            Guardar IMEIs
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import SearchFilter from '@/Shared/SearchFilter.vue'
import pickBy from 'lodash/pickBy'
import debounce from 'lodash/debounce'

export default {
  components: {
    Head,
    Link,
    TextInput,
    SelectInput,
    SearchFilter,
  },
  layout: Layout,
  
  props: {
    products: Object,
    deposits: Array,
  },

  data() {
    return {
      quantities: this.$inertia.form({
        qtys: [],
        products: [],
        deposits: [],
        imeis: {},
      }),

      imeiModal: {
        show: false,
        product: null,
        imeis: []
      },

      form: this.$inertia.form({
        search: {
          terms: '',
          deposit: '',
          brand_id: '',
          part_id: '',
          truck_model_id: '',
          region_id: '',
        },
      }),

      items: []
    }
  },

  watch: {
    'form.search': {
      handler: debounce(function() {
        this.searchProducts()
      }, 300),
      deep: true
    }
  },

  methods: {
    back() {
      window.history.back()
    },

    searchProducts() {
      this.$inertia.get('/inventories/create', 
        pickBy({ search: this.form.search }), 
        {
          preserveState: true,
          preserveScroll: true,
          replace: true
        }
      )
    },

    openImeiModal(product) {
      this.imeiModal.product = product;
      
      let qty = Number(this.quantities.qtys[product.id]) || 0;
      let existingImeis = this.quantities.imeis[product.id] || [];
      
      this.imeiModal.imeis = Array.from({ length: qty }, (_, i) => existingImeis[i] || '');
      this.imeiModal.show = true;
      this.$nextTick(() => {
        const firstInput = document.getElementById('inv-imei-1');
        if (firstInput) firstInput.focus();
      });
    },

    closeImeiModal() {
      this.imeiModal.show = false;
      this.imeiModal.product = null;
      this.imeiModal.imeis = [];
    },

    handleImeiEnter(index) {
      const currentVal = (this.imeiModal.imeis[index - 1] || '').trim();
      if (currentVal && !/^\d{15}$/.test(currentVal)) {
        this.$swal?.fire({
          icon: 'warning',
          title: 'IMEI inválido',
          text: `El código escaneado "${currentVal}" no tiene 15 dígitos numéricos (tiene ${currentVal.length} caracteres).`,
        }) || alert(`El código escaneado "${currentVal}" no tiene 15 dígitos numéricos.`);
        return;
      }

      const total = Number(this.quantities.qtys[this.imeiModal.product?.id]) || 0;
      if (index < total) {
        this.$nextTick(() => {
          const nextInput = document.getElementById(`inv-imei-${index + 1}`);
          if (nextInput) nextInput.focus();
        });
      } else {
        this.saveImeis();
      }
    },

    saveImeis() {
      const filledImeis = this.imeiModal.imeis.map(i => (i || '').trim()).filter(i => i !== '');
      const requiredQty = Number(this.quantities.qtys[this.imeiModal.product.id]) || 0;
      
      if (filledImeis.length !== requiredQty) {
        this.$swal?.fire({
          icon: 'error',
          title: 'Error',
          text: 'Debe ingresar todos los IMEIs correspondientes a la cantidad.',
        }) || alert('Debe ingresar todos los IMEIs correspondientes a la cantidad.');
        return;
      }

      // Validar que todos tengan exactamente 15 dígitos numéricos
      const invalidImeis = filledImeis.filter(i => !/^\d{15}$/.test(i));
      if (invalidImeis.length > 0) {
        this.$swal?.fire({
          icon: 'error',
          title: 'IMEI inválido',
          text: `Los siguientes códigos no son válidos: ${invalidImeis.join(', ')}. Los IMEIs deben tener exactamente 15 dígitos numéricos.`,
        }) || alert(`Los siguientes códigos no son válidos: ${invalidImeis.join(', ')}. Los IMEIs deben tener exactamente 15 dígitos numéricos.`);
        return;
      }
      
      const uniqueImeis = new Set(filledImeis);
      if (uniqueImeis.size !== filledImeis.length) {
        this.$swal?.fire({
          icon: 'error',
          title: 'Error',
          text: 'Hay IMEIs duplicados en la lista.',
        }) || alert('Hay IMEIs duplicados en la lista.');
        return;
      }

      this.quantities.imeis[this.imeiModal.product.id] = filledImeis;
      this.closeImeiModal();
    },

    addProduct(product) {
      if (!this.items.find(item => item.id === product.id)) {
        this.items.push(product)
        this.$nextTick(() => {
          this.quantities.qtys[product.id] = 1
        })
      }
    },

    removeProduct(product) {
      if (confirm('¿Está seguro que desea eliminar este producto?')) {
        this.items = this.items.filter(item => item.id !== product.id)
        delete this.quantities.qtys[product.id]
        delete this.quantities.deposits[product.id]
      }
    },

    generate() {
      if (!this.items.length) {
        alert('Debe agregar al menos un producto')
        return
      }

      this.quantities.products = this.items.map(item => item.id)
      this.quantities.post('/inventories')
    }
  }
}
</script>
